import { trackEvent } from '.';

// Todos los tiers sampleados bajaron a la mitad en ago/2026: el evento seguía siendo
// el segundo más caro de todo el proyecto (24,4k/semana solo desde Flash, ~104k/mes)
// contra el free tier de 1M de PostHog, y es telemetría pura. Cada muestra sale con
// `sample_rate`, así que la extrapolación no cambia y los percentiles por ruta
// tampoco: el bucket más flaco después del recorte (critical_action con éxito) sigue
// dando ~1,3k muestras/semana, y los otros tres ~2,5k cada uno.
// Recorte sep/2026: SOLO los tiers de ÉXITO bajan otra vez a la mitad. El evento
// seguía siendo el tercero más caro del proyecto (~124,7k/mes) y el 61% de eso es
// `success` — puro denominador. Los tiers de FALLA quedan intactos, ver abajo.
const NORMAL_SAMPLE_RATE = 0.00125;
// `slow` seguía siendo el bucket más pesado del evento aun al 10%: 12.294/semana
// medidos el 6/ago/2026 (37,9% del evento). Al 5% quedaban ~6k/semana y al 2,5%
// ~3k, que sobran para percentiles por ruta — el p50/p90 no mejora por duplicar
// la muestra.
// Oct/2026: slow y very_slow a la mitad otra vez (eran 3,4k y 4,1k filas en 13 días,
// las dos más pesadas de los tiers de éxito). Siguen quedando ~1,7k y ~2k muestras
// cada 13 días, que alcanzan para el p50/p90 por ruta de una semana.
const SLOW_SAMPLE_RATE = 0.00625;
// critical_action y very_slow al 100% eran ~95k eventos/mes (los 2 tiers más
// pesados del evento) contra el free tier de 1M de PostHog. Los errores siguen
// sin samplear — un fallo de una acción crítica nunca se pierde; para leer
// latencias alcanza una muestra (todo sale con sample_rate para extrapolar).
const CRITICAL_SUCCESS_SAMPLE_RATE = 0.025;
const VERY_SLOW_SAMPLE_RATE = 0.03125;
// Los fallos de RED de fondo (no de acción crítica) son 5.920/semana al 100%: casi
// todos el mismo cluster conocido de `/client/me/vendor` en webview. Para saber que
// existe y seguir su tendencia alcanza de sobra con 12,5% (~740/semana). Los errores
// de una acción crítica y los server_error de acción crítica NO se samplean: siguen
// al 100% más abajo, porque ahí cada caso individual importa.
//
// NO BAJAR ESTE RATE, ni el 100% de la PRIMERA network_error en superficie crítica
// (las réplicas de una misma ráfaga sí se samplean, ver abajo). En el recorte
// de sep/2026 fue lo primero que miré y lo único que dejé quieto: las dos alertas
// (`ALERTA · tasa de fallas última hora completa` y `· p50 de errores de red`) gatean
// con `affected_people >= 10` POR HORA, y ese contador cuenta personas de la MUESTRA,
// no extrapoladas. Con 3.340 personas en 14 días corre en ~10/h, o sea justo en el
// filo: samplear a la mitad no vuelve la alerta menos sensible, la APAGA. El resto de
// esos insights sí sobrevive a cualquier sampling porque extrapola con
// `sum(1 / sample_rate)` — el gate de personas es la excepción.
const BACKGROUND_ERROR_SAMPLE_RATE = 0.125;
// Ráfagas de network_error en superficie crítica (oct/2026). Cuando la red se cae o
// el SO suspende la pestaña, TODAS las requests en vuelo fallan juntas y cada una
// salía al 100%: 19,8k filas en 13 días que colapsan a 6,5k si se cuenta una por
// dispositivo cada 5 minutos — era el 5% de todo el proyecto. La PRIMERA falla de
// cada ventana sigue al 100%, así que `affected_people` (personas de la muestra que
// gatean las alertas) no cambia: toda persona con una falla en la hora sigue
// apareciendo. Las siguientes de la misma ventana van al 12,5% con su `sample_rate`,
// así que `estimated_failures` y la tasa de fallas (ponderadas con
// `sum(1 / sample_rate)`) no se mueven en promedio. Lo único que sí cambia es lo
// que se lea SIN ponderar (p. ej. el p50 crudo de `RUM · latencia de errores de
// red`), donde las réplicas de una misma caída pesan menos.
const NETWORK_ERROR_BURST_WINDOW_MS = 5 * 60 * 1000;
const NETWORK_ERROR_BURST_SAMPLE_RATE = 0.125;
let lastFullRateNetworkErrorAt = Number.NEGATIVE_INFINITY;

const criticalNetworkErrorSampling = (): { rate: number; tier: string } => {
  const now = Date.now();
  if (now - lastFullRateNetworkErrorAt < NETWORK_ERROR_BURST_WINDOW_MS) {
    return { rate: NETWORK_ERROR_BURST_SAMPLE_RATE, tier: 'critical_surface_burst' };
  }
  lastFullRateNetworkErrorAt = now;
  return { rate: 1, tier: 'critical_surface' };
};
const SLOW_REQUEST_MS = 750;
const VERY_SLOW_REQUEST_MS = 2000;

type ApiOutcome = 'success' | 'client_error' | 'server_error' | 'network_error';

export interface ApiPerformanceMeasurement {
  endpoint: string;
  method: string;
  durationMs: number;
  authDurationMs: number;
  networkDurationMs: number;
  parseDurationMs: number;
  response?: Response;
  retried401: boolean;
  errorName?: string;
  pageVisibilityAtStart: DocumentVisibilityState;
  pageVisibilityAtEnd: DocumentVisibilityState;
  pageHiddenDuringRequest: boolean;
}

const normalizeSegment = (segment: string): string => {
  if (/^\d+$/.test(segment)) return ':id';
  if (/^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(segment)) return ':uuid';
  if (/^[0-9a-f]{24,}$/i.test(segment)) return ':token';
  if (segment.includes('@')) return ':email';
  return segment;
};

export const normalizePerformancePath = (value: string): string => {
  const pathname = value.split('?')[0].split('#')[0];
  return `/${pathname.split('/').filter(Boolean).map(normalizeSegment).join('/')}`;
};

const fallbackApiRoute = (endpoint: string): string => {
  const segments = normalizePerformancePath(endpoint).split('/').filter(Boolean);
  if (segments[0] === 'me' && segments[1]) return `/client/me/${segments[1]}`;
  return `/client/${segments[0] || 'root'}`;
};

const classifyCriticalAction = (endpoint: string, method: string): string | undefined => {
  const path = normalizePerformancePath(endpoint).replace(/^\/v1/, '');
  const verb = method.toUpperCase();

  if (verb === 'POST' && /^\/bookings\/(multi\/)?event_instances\/book$/.test(path)) {
    return path.includes('/multi/') ? 'booking_create_multi' : 'booking_create';
  }
  if (verb === 'POST' && /^\/bookings\/:id\/checkout$/.test(path)) return 'booking_checkout';
  if (verb === 'POST' && /^\/events\/instances\/:id\/reschedule$/.test(path)) return 'booking_reschedule';
  if (verb === 'PUT' && path === '/me/vendor/storefront') return 'storefront_update';
  if (verb === 'POST' && path === '/me/vendor/gallery/image-upload-url') {
    return 'storefront_gallery_image_prepare';
  }
  if (verb === 'POST' && path === '/me/vendor/gallery/image-upload-complete') {
    return 'storefront_gallery_image_process';
  }
  if ((verb === 'POST' || verb === 'PUT') && /^\/services(\/(:id))?$/.test(path)) {
    return verb === 'POST' ? 'service_create' : 'service_update';
  }
  if (verb === 'PUT' && /^\/services\/:id\/uploadImage$/.test(path)) return 'service_image_upload';

  return undefined;
};

const outcomeFor = (response?: Response): ApiOutcome => {
  if (!response) return 'network_error';
  if (response.status >= 500) return 'server_error';
  if (response.status >= 400) return 'client_error';
  return 'success';
};

const isCriticalSurface = (endpoint: string): boolean => {
  const path = normalizePerformancePath(endpoint).replace(/^\/v1/, '');
  return path === '/sessions' || path === '/me/vendor' || path.startsWith('/me/vendor/');
};

const samplingFor = (
  durationMs: number,
  outcome: ApiOutcome,
  criticalAction: string | undefined,
  endpoint: string
): { rate: number; tier: string } => {
  if (criticalAction) {
    if (outcome !== 'success') return { rate: 1, tier: 'critical_action' };
    return { rate: CRITICAL_SUCCESS_SAMPLE_RATE, tier: 'critical_action' };
  }
  // `server_error` sigue al 100%: son 55/semana y cada 5xx es accionable. El que se
  // samplea es `network_error`, que a 5.920/semana era el 18% de todo el evento.
  if (outcome === 'server_error') return { rate: 1, tier: 'error' };
  if (outcome === 'network_error') {
    // El bootstrap es nuestro detector temprano: perder una muestra de /sessions o
    // /me/vendor puede ocultar exactamente el incidente que deja la app congelada.
    return isCriticalSurface(endpoint)
      ? criticalNetworkErrorSampling()
      : { rate: BACKGROUND_ERROR_SAMPLE_RATE, tier: 'error' };
  }
  if (durationMs >= VERY_SLOW_REQUEST_MS) return { rate: VERY_SLOW_SAMPLE_RATE, tier: 'very_slow' };
  if (durationMs >= SLOW_REQUEST_MS) return { rate: SLOW_SAMPLE_RATE, tier: 'slow' };
  return { rate: NORMAL_SAMPLE_RATE, tier: 'baseline' };
};

/**
 * Sends a bounded, low-cardinality client-to-API timing event. Cyclone's stable
 * route template wins over the raw client endpoint once that response header is
 * available, and x-trace-id links the browser sample to the Jaeger trace.
 */
export const recordApiPerformance = (measurement: ApiPerformanceMeasurement): void => {
  if (import.meta.env.VITE_NODE_ENV !== 'production') return;

  const { response } = measurement;
  const outcome = outcomeFor(response);
  const criticalAction = classifyCriticalAction(measurement.endpoint, measurement.method);
  const sampling = samplingFor(measurement.durationMs, outcome, criticalAction, measurement.endpoint);
  if (Math.random() > sampling.rate) return;

  const serverRoute = response?.headers.get('x-route-template');
  const traceId = response?.headers.get('x-trace-id');
  const responseLength = response?.headers.get('content-length');
  const responseBytes = responseLength ? Number(responseLength) : undefined;
  const connection = (
    navigator as Navigator & { connection?: { effectiveType?: string; saveData?: boolean } }
  ).connection;

  trackEvent('api_request_performance', {
    screen: 'API Performance',
    source: 'flash',
    action: criticalAction || 'api_request',
    page_path: normalizePerformancePath(window.location.pathname),
    http_route: serverRoute || fallbackApiRoute(measurement.endpoint),
    http_method: measurement.method.toUpperCase(),
    http_status_code: response?.status || 0,
    outcome,
    duration_ms: Math.round(measurement.durationMs),
    auth_duration_ms: Math.round(measurement.authDurationMs),
    network_duration_ms: Math.round(measurement.networkDurationMs),
    parse_duration_ms: Math.round(measurement.parseDurationMs),
    retry_401: measurement.retried401,
    trace_id: traceId || undefined,
    response_size_bytes:
      responseBytes !== undefined && Number.isFinite(responseBytes) ? responseBytes : undefined,
    error_type: measurement.errorName,
    page_visibility_start: measurement.pageVisibilityAtStart,
    page_visibility_end: measurement.pageVisibilityAtEnd,
    page_hidden_during_request: measurement.pageHiddenDuringRequest,
    device: window.matchMedia('(max-width: 640px)').matches ? 'mobile' : 'desktop',
    connection: connection?.effectiveType,
    save_data: connection?.saveData ?? false,
    sample_rate: sampling.rate,
    sample_tier: sampling.tier
  });
};
